/**
 * Admin API base URL resolution (shared by runtime app and env-config generator).
 */

/** Default admin API for hosted development (dev.jobscribe.in). */
export const DEFAULT_DEV_API_BASE_URL = "https://dev-admin.jobscribe.in/api";

/** Default admin API for live production (jobscribe.in). */
export const DEFAULT_PROD_API_BASE_URL = "https://app.jobscribe.in/api";

/** @deprecated use DEFAULT_PROD_API_BASE_URL */
export const FALLBACK_PROD_API_BASE_URL = DEFAULT_PROD_API_BASE_URL;

const DEV_ADMIN_API_HOST = "dev-admin.jobscribe.in";
const PROD_ADMIN_API_HOST = "app.jobscribe.in";

/** Marketing / public site hosts — /api on these serves the React SPA, not JSON. */
const MARKETING_HOSTS = new Set([
  "dev.jobscribe.in",
  "jobscribe.in",
  "www.jobscribe.in",
]);

export function sanitizeEnvUrl(raw) {
  let value = String(raw ?? "").trim();
  if (!value) return "";

  while (
    (value.startsWith('"') && value.endsWith('"')) ||
    (value.startsWith("'") && value.endsWith("'"))
  ) {
    value = value.slice(1, -1).trim();
  }

  if (value.startsWith('"') || value.startsWith("'")) {
    value = value.slice(1).trim();
  }

  if (!/^https?:\/\//i.test(value) && /jobscribe\.in|localhost/i.test(value)) {
    value = `https://${value.replace(/^\/+/, "")}`;
  }

  return value.replace(/\/$/, "");
}

function isLocalApiHost(hostname) {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "[::1]"
  );
}

/**
 * Use Vite's /api proxy only when the app is served from localhost.
 * Do not use import.meta.env.DEV — a server build with NODE_ENV=development in .env
 * bakes DEV=true and breaks hosted careers (fetch goes to /api on dev.jobscribe.in → HTML).
 */
export function shouldUseViteApiProxy() {
  if (typeof window === "undefined") return false;
  return isLocalApiHost(window.location.hostname);
}

/**
 * @param {string} configuredRaw - value from VITE_API_BASE_URL
 * @param {{ useViteProxy?: boolean }} [options]
 */
export function resolveApiBaseUrl(configuredRaw, { useViteProxy = false } = {}) {
  if (useViteProxy) {
    return "/api";
  }

  const configured = sanitizeEnvUrl(configuredRaw);
  if (configured && /^https?:\/\//i.test(configured)) {
    try {
      const url = new URL(configured);
      if (!isLocalApiHost(url.hostname)) {
        if (MARKETING_HOSTS.has(url.hostname)) {
          const adminHost =
            url.hostname === "dev.jobscribe.in"
              ? DEV_ADMIN_API_HOST
              : PROD_ADMIN_API_HOST;
          return `https://${adminHost}/api`;
        }
        return configured;
      }
    } catch {
      // use fallback
    }
  }

  return DEFAULT_PROD_API_BASE_URL.replace(/\/$/, "");
}
